import { Injectable } from '@angular/core';
import { BaseApiService } from './common/base-api.service';
import { API } from '../constants/api';
import { HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { RvsfFormValue } from '../models/rvsf-list.model';
import { RvsfMapper } from '../mapper/rvsf-list.mapper';
import {
  RvsfBranchFormValue,
  RvsfBranchRequest,
} from '../models/rvsf-branch.model';
import { RvsfBranchMapper } from '../mapper/rvsf-branch.mapper';
import { RvsfUserFormValue } from '../models/rvsf-user.model';
import { RvsfUserMapper } from '../mapper/rvsf-user.mapper';
@Injectable({
  providedIn: 'root',
})
export class RvsfService {
  constructor(private baseApiService: BaseApiService) {}

  userLogout(): Observable<any> {
    const body = {};
    return this.baseApiService.post(API.rvsf.logout, body);
  }

  searchRvsf(
    requestObj: any,
    params?: HttpParams | { [param: string]: string | string[] },
  ): Observable<any> {
    return this.baseApiService.postRequestWithFormData(
      API.rvsf.search,
      requestObj,
      params,
    );
  }

  getRvsfName(prefix: string): Observable<any> {
    const body = { rvsf_name: prefix || '' };
    const params = new HttpParams()
      .set('orderByCol', 'rvsf_id')
      .set('orderByDir', 'desc')
      .set('pageSize', 5)
      .set('pageNumber', 1);

    return this.baseApiService.post(API.rvsf.search, body, params);
  }

  createRvsf(formValue: RvsfFormValue, isEdit: boolean) {
    const formData = new FormData();

    // TEXT FIELDS
    if (formValue.id) formData.append('rvsf_id', String(formValue.id));

    formData.append('rvsf_name', formValue.name.trim());
    formData.append('rvsf_legal_name', formValue.legalName.trim());
    formData.append('rvsf_contact_mobile_no', formValue.mobileNumber.trim());
    formData.append('rvsf_contact_email_id', formValue.emailId.trim());
    formData.append('rvsf_address_line_1', formValue.addressLine1.trim());
    formData.append(
      'rvsf_address_line_2',
      formValue.addressLine2?.trim() || '',
    );
    formData.append('rvsf_pincode_id', String(formValue.pincode));
    formData.append(
      'landing_page_about_us_text',
      formValue.landingPageAboutText.trim(),
    );
    formData.append('access_url', formValue.accessUrl?.trim() || '');

    // LOGO
    if ((formValue.logoPath as any) instanceof File) {
      formData.append('rvsfLogoFile', formValue.logoPath);
    }

    // FAVICON
    if ((formValue.faviconPath as any) instanceof File) {
      formData.append('rvsfFeviconFile', formValue.faviconPath);
    }

    return this.baseApiService.postRequestWithFormData(API.rvsf.save, formData);
  }

  updateRvsf(id: number, formValue: RvsfFormValue, isEdit: boolean) {
    const formData = new FormData();

    // TEXT FIELDS
    if (formValue.id) formData.append('rvsf_id', String(formValue.id));
    formData.append('rvsf_id', String(formValue.id));
    formData.append('rvsf_name', formValue.name.trim());
    formData.append('rvsf_legal_name', formValue.legalName.trim());
    formData.append('rvsf_contact_mobile_no', formValue.mobileNumber.trim());
    formData.append('rvsf_contact_email_id', formValue.emailId.trim());
    formData.append('rvsf_address_line_1', formValue.addressLine1.trim());
    formData.append(
      'rvsf_address_line_2',
      formValue.addressLine2?.trim() || '',
    );
    formData.append('access_url', formValue.accessUrl?.trim() || '');
    formData.append('rvsf_pincode_id', String(formValue.pincode));
    formData.append(
      'landing_page_about_us_text',
      formValue.landingPageAboutText.trim(),
    );

    // LOGO
    if ((formValue.logoPath as any) instanceof File) {
      formData.append('rvsfLogoFile', formValue.logoPath);
    }
    // FAVICON
    if ((formValue.faviconPath as any) instanceof File) {
      formData.append('rvsfFeviconFile', formValue.faviconPath);
    }
    // ✅ Call API with ID
    return this.baseApiService.put(`${API.rvsf.update}/${id}`, formData);
  }

  getRvsfById(id: number): Observable<RvsfFormValue> {
    const apiUrl = `${API.rvsf.get}/${id}`;
    return this.baseApiService
      .get(apiUrl)
      .pipe(map((res: any) => RvsfMapper.toForm(res.data ?? res)));
  }

  searchPincode(code: string) {
    const params = {
      pincode: code,
    };
    return this.baseApiService.get(API.rvsf.pinCode, params);
  }

  searchBranch(
    requestObj: any,
    params?: HttpParams | { [param: string]: string | string[] },
  ): Observable<any> {
    return this.baseApiService.postRequestWithFormData(
      API.branch.search,
      requestObj,
      params,
    );
  }

  createBranch(formValue: RvsfBranchFormValue): Observable<any> {
    const request: RvsfBranchRequest = RvsfBranchMapper.toRequest(formValue);

    const formData = new FormData();

    // TEXT FIELDS except files
    Object.entries(request).forEach(([key, value]) => {
      if (
        key !== 'gst_document_file' &&
        key !== 'cin_file' &&
        key !== 'authorized_signature'
      ) {
        formData.append(key, value ?? '');
      }
    });

    // Append files explicitly
    if (formValue.gstPath instanceof File) {
      formData.append('gst_document_file', formValue.gstPath);
    }

    if (formValue.cinPath instanceof File) {
      formData.append('cin_file', formValue.cinPath);
    }
    if (formValue.signaturePath instanceof File) {
      formData.append('authorized_signature', formValue.signaturePath);
    }
    return this.baseApiService.postRequestWithFormData(
      API.branch.save,
      formData,
    );
  }

  getBranchById(id: number): Observable<RvsfBranchFormValue> {
    const apiUrl = `${API.branch.get}/${id}`;
    return this.baseApiService
      .get(apiUrl)
      .pipe(map((res: any) => RvsfBranchMapper.toForm(res.data ?? res)));
  }

  updateBranch(id: number, formValue: RvsfBranchFormValue): Observable<any> {
    const request: RvsfBranchRequest = RvsfBranchMapper.toRequest(formValue);

    const formData = new FormData();

    // TEXT FIELDS except files
    Object.entries(request).forEach(([key, value]) => {
      if (
        key !== 'gst_document_file' &&
        key !== 'cin_file' &&
        key !== 'authorized_signature'
      ) {
        formData.append(key, value ?? '');
      }
    });

    // Append files explicitly
    if (formValue.gstPath instanceof File) {
      formData.append('gst_document_file', formValue.gstPath);
    }

    if (formValue.cinPath instanceof File) {
      formData.append('cin_file', formValue.cinPath);
    }
    if (formValue.signaturePath instanceof File) {
      formData.append('authorized_signature', formValue.signaturePath);
    }

    return this.baseApiService.put(`${API.branch.update}/${id}`, formData);
  }

  searchRvsfUser(
    requestObj: any,
    params?: HttpParams | { [param: string]: string | string[] },
  ): Observable<any> {
    return this.baseApiService.postRequestWithFormData(
      API.rvsfuser.search,
      requestObj,
      params,
    );
  }

  getBranchName(prefix: string, rvsfId?: number): Observable<any> {
    const body: any = {
      branch_name: prefix || '',
    };
    if (rvsfId) {
      body.rvsf_id = rvsfId;
    }

    const params = new HttpParams()
      .set('orderByCol', 'branch_id')
      .set('orderByDir', 'desc')
      .set('pageSize', 5)
      .set('pageNumber', 1);

    return this.baseApiService.post(API.branch.search, body, params);
  }

  saveRvsfUser(formValue: RvsfUserFormValue) {
    const formData = new FormData();

    formData.append('full_name', formValue.fullName.trim());
    formData.append('email', formValue.email.trim());
    formData.append('password', formValue.password);
    formData.append('designation', formValue.designation.trim());
    formData.append('mobile_number', formValue.mobileNumber.trim());
    formData.append('role_id', String(formValue.roleId));
    formData.append('rvsf_id', String(formValue.rvsfId));
    formData.append('is_administrator', String(formValue.isAdministrator));

    // ✅ MULTIPLE branch_ids[index]
    formValue.branchIds.forEach((id, index) => {
      formData.append(`branch_ids[${index}]`, String(id));
    });

    if (formValue.profilePictureFile instanceof File) {
      formData.append('profilePicturePathFile', formValue.profilePictureFile);
    }

    return this.baseApiService.postRequestWithFormData(
      API.rvsfuser.save,
      formData,
    );
  }
  getRoles(prefix: string, rvsfId?: number): Observable<any> {
    const body: any = {
      role_name: prefix,
    };

    if (rvsfId) {
      body.rvsf_id = rvsfId;
    }

    const params = new HttpParams()
      .set('pageNumber', '1')
      .set('pageSize', '10')
      .set('orderByCol', 'role_id')
      .set('orderByDir', 'DESC');

    return this.baseApiService.post(API.rvsfuser.searchRole, body, params);
  }

  getUserById(id: number): Observable<RvsfUserFormValue> {
    const apiUrl = `${API.rvsfuser.get}/${id}`;
    return this.baseApiService
      .get(apiUrl)
      .pipe(map((res: any) => RvsfUserMapper.toForm(res.data ?? res)));
  }

  updateUser(id: number, formValue: RvsfUserFormValue): Observable<any> {
    const formData = new FormData();

    formData.append('full_name', formValue.fullName.trim());
    formData.append('email', formValue.email.trim());
    formData.append('designation', formValue.designation.trim());
    formData.append('mobile_number', formValue.mobileNumber.trim());
    formData.append('role_id', String(formValue.roleId));
    formData.append('rvsf_id', String(formValue.rvsfId));
    formData.append('is_administrator', String(formValue.isAdministrator));

    // Only append password if it's provided (to avoid overwriting existing password)
    if (formValue.password && formValue.password.trim() !== '') {
      formData.append('password', formValue.password.trim());
    }

    if (formValue.profilePictureFile instanceof File) {
      formData.append('profilePicturePathFile', formValue.profilePictureFile);
    }

    // MULTIPLE branch_ids[index]
    formValue.branchIds?.forEach((branchId, index) => {
      formData.append(`branch_ids[${index}]`, String(branchId));
    });

    return this.baseApiService.put(`${API.rvsfuser.update}/${id}`, formData);
  }

  getBranch(prefix: string): Observable<any> {
    const body = { branch_name: prefix || '' };
    const params = new HttpParams()
      .set('orderByCol', 'branch_id')
      .set('orderByDir', 'desc')
      .set('pageSize', 5)
      .set('pageNumber', 1);

    return this.baseApiService.post(API.branch.search, body, params);
  }
}
